/* eslint-disable no-console */

/**
 * Temporary kill switch: set to true when the app should manage the service worker again.
 * When false: no new registration; existing workers are unregistered once so stale caches
 * cannot keep serving a white-screen build.
 */
export const SERVICE_WORKER_ENABLED = false;

const SW_DISABLED_RELOAD_KEY = 'gpr-sw-disabled-reload';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

/**
 * One-shot cleanup while SW is disabled: unregister all workers, clear Cache Storage,
 * and reload once if a controller was still active (so the next load is network-only).
 */
export function disableServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return Promise.resolve();
  }

  const hadController = Boolean(navigator.serviceWorker.controller);

  return navigator.serviceWorker
    .getRegistrations()
    .then((registrations) =>
      Promise.all(
        registrations.map((registration) => registration.unregister())
      )
    )
    .then(() => {
      if (!('caches' in window)) {
        return undefined;
      }

      return caches
        .keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
    })
    .then(() => {
      if (!hadController) return;

      // Avoid an infinite reload if something re-registers from an old tab.
      if (sessionStorage.getItem(SW_DISABLED_RELOAD_KEY)) return;

      sessionStorage.setItem(SW_DISABLED_RELOAD_KEY, '1');
      window.location.reload();
    })
    .catch((err) => {
      console.error('Failed to disable service worker:', err);
    });
}

export function applyUpdate(registration) {
  if (!SERVICE_WORKER_ENABLED) return;

  if (registration?.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

export function checkForUpdate() {
  if (!SERVICE_WORKER_ENABLED || !('serviceWorker' in navigator)) {
    return Promise.resolve();
  }

  return navigator.serviceWorker.getRegistration().then((registration) => {
    if (!registration) {
      return undefined;
    }

    return registration.update();
  });
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const worker = registration.installing;
        if (!worker) return;

        worker.onstatechange = () => {
          if (worker.state !== 'installed') return;

          // FIRST INSTALL (PWA ready)
          if (!navigator.serviceWorker.controller) {
            console.info('Content cached for offline use.');
            config?.onSuccess?.(registration);
            return;
          }

          // UPDATE AVAILABLE
          console.info('New version available.');

          config?.onUpdate?.(registration);
        };
      };
    })
    .catch((err) => {
      console.error('SW registration failed:', err);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');

      if (
        response.status === 404 ||
        (contentType && !contentType.includes('javascript'))
      ) {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (!registration) return;
          registration.unregister().then(() => window.location.reload());
        });
        return;
      }

      registerValidSW(swUrl, config);
    })
    .catch(() => {
      console.info('Offline mode detected.');
    });
}

export function register(config) {
  if (!SERVICE_WORKER_ENABLED) {
    disableServiceWorker();
    return;
  }

  // if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

export function unregister() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) =>
      Promise.all(
        registrations.map((registration) => registration.unregister())
      )
    )
    .catch((err) => console.error(err));
}
